import type { SxProps, Theme } from '@mui/material/styles'
import { palettePrimaryMain } from '@/theme/designTokens'

/**
 * Shared library card hover — N4 Inset press.
 * Soft lift + slightly thicker border on hover; soft inset shadow on press.
 * Used by Question Bank, Template Library, campaigns, publish selection, and related cards.
 */
export const libraryCardHoverSx: SxProps<Theme> = {
  transition:
    'border-color 180ms cubic-bezier(0.22, 1, 0.36, 1), border-width 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms cubic-bezier(0.22, 1, 0.36, 1), transform 180ms cubic-bezier(0.22, 1, 0.36, 1)',
  '&:hover': {
    borderWidth: '1.8px',
    borderStyle: 'solid',
    borderColor: '#cbd5e1',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
  },
  '&:active': {
    borderWidth: '1.8px',
    borderStyle: 'solid',
    transform: 'translateY(-1px)',
    boxShadow: 'inset 0 1px 3px rgba(15, 23, 42, 0.06), 0 2px 8px rgba(15, 23, 42, 0.04)',
  },
  '&:focus-visible': {
    outline: `2px solid ${palettePrimaryMain}`,
    outlineOffset: 2,
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    transform: 'none',
  },
}

/** Create Instrument tiles — primary border + tint shadow on hover (BFP-703). */
export const createInstrumentTileHoverSx: SxProps<Theme> = {
  transition:
    'border-color 180ms cubic-bezier(0.22, 1, 0.36, 1), border-width 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms cubic-bezier(0.22, 1, 0.36, 1)',
  '&:hover, &[data-tile-hovered]': {
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: palettePrimaryMain,
    boxShadow: '0px 4px 10px rgba(0, 140, 208, 0.12)',
  },
  '&:active, &[data-tile-pressed]': {
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: palettePrimaryMain,
    boxShadow: '0px 4px 10px rgba(0, 140, 208, 0.12)',
  },
  '&:focus-visible': {
    outline: `2px solid ${palettePrimaryMain}`,
    outlineOffset: 2,
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}

/** Bookmark / save icon on library cards and in Question Builder — light blue hover wash. */
export const libraryBookmarkIconButtonSx: SxProps<Theme> = {
  color: '#008cd0',
  borderRadius: '8px',
  transition: 'background-color 180ms cubic-bezier(0.22, 1, 0.36, 1)',
  '&:hover': {
    bgcolor: 'rgba(0, 140, 208, 0.08)',
  },
  '&:active': {
    bgcolor: 'rgba(0, 140, 208, 0.12)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}

/** Question Builder — bordered save-to-Question-Bank control (matches Add to Library hover). */
export const questionBankSaveBookmarkButtonSx: SxProps<Theme> = {
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  width: 36,
  height: 36,
  transition:
    'background-color 180ms cubic-bezier(0.22, 1, 0.36, 1), border-color 180ms cubic-bezier(0.22, 1, 0.36, 1), color 180ms cubic-bezier(0.22, 1, 0.36, 1)',
  '&:hover': {
    bgcolor: 'rgba(0, 140, 208, 0.04)',
    borderColor: '#008cd0',
    color: '#008cd0',
  },
  '&:active': {
    bgcolor: 'rgba(0, 140, 208, 0.08)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}
